import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Daily from "@daily-co/daily-js";
import { toast } from "sonner";
import {
  Mic, MicOff, Video as VideoIcon, VideoOff, SwitchCamera, PhoneOff,
  MessageSquare, NotebookPen, Flag, Signal, ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";

/** /live/:apptId — BioHacker Live Coach™ room. Preview → live 1-to-1 video with
 * chat, coach notes, and end-session recap. */
export default function LiveRoom() {
  const { apptId } = useParams();
  const nav = useNavigate();
  const callRef = useRef(null);
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const credsRef = useRef(null);
  const [phase, setPhase] = useState("loading"); // loading | preview | live | ended | error
  const [error, setError] = useState("");
  const [appt, setAppt] = useState(null);
  const [role, setRole] = useState("athlete");
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [remoteHere, setRemoteHere] = useState(false);
  const [devices, setDevices] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const [quality, setQuality] = useState("good");
  const [chatOpen, setChatOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  const attachTracks = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const parts = call.participants();
    const localT = parts.local?.tracks?.video?.persistentTrack;
    if (localRef.current) localRef.current.srcObject = localT ? new MediaStream([localT]) : null;
    const remote = Object.values(parts).find((p) => !p.local);
    setRemoteHere(!!remote);
    if (remoteRef.current) {
      const tracks = [];
      const rv = remote?.tracks?.video?.persistentTrack;
      const ra = remote?.tracks?.audio?.persistentTrack;
      if (rv) tracks.push(rv);
      if (ra) tracks.push(ra);
      remoteRef.current.srcObject = tracks.length ? new MediaStream(tracks) : null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        // StrictMode-safe: wait out any prior instance before creating a new one
        for (let i = 0; i < 12 && Daily.getCallInstance(); i++) {
          try { await Daily.getCallInstance().destroy(); }
          catch { await new Promise((r) => setTimeout(r, 150)); }
        }
        if (cancelled) return;
        const call = Daily.createCallObject({ subscribeToTracksAutomatically: true });
        callRef.current = call;
        const sync = () => !cancelled && attachTracks();
        call.on("participant-joined", sync).on("participant-left", sync)
          .on("participant-updated", sync).on("track-started", sync).on("track-stopped", sync)
          .on("network-quality-change", (e) => !cancelled && setQuality(e.threshold || "good"))
          .on("error", (e) => { if (!cancelled) { setError(e?.errorMsg || "Call error"); setPhase("error"); } });
        const { data } = await api.post(`/livecoach/appointments/${apptId}/room`);
        if (cancelled) return;
        credsRef.current = data;
        setAppt(data.appointment);
        setRole(data.role);
        await call.startCamera();
        const { devices: d } = await call.enumerateDevices();
        if (!cancelled) { setDevices(d); attachTracks(); setPhase("preview"); }
      } catch (er) {
        if (!cancelled) { setError(formatApiError(er) || er?.errorMsg || String(er)); setPhase("error"); }
      }
    };
    init();
    return () => {
      cancelled = true;
      const c = callRef.current;
      callRef.current = null;
      if (c) { c.removeAllListeners(); c.destroy().catch(() => {}); }
    };
  }, [apptId, attachTracks]);

  useEffect(() => {
    if (phase !== "live") return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [phase]);

  const join = async () => {
    try {
      await callRef.current.join({ url: credsRef.current.url, token: credsRef.current.token });
      setPhase("live");
      attachTracks();
    } catch (er) { setError(er?.errorMsg || String(er)); setPhase("error"); }
  };

  const toggleMic = () => { const c = callRef.current; const next = !c.localAudio(); c.setLocalAudio(next); setMicOn(next); };
  const toggleCam = () => { const c = callRef.current; const next = !c.localVideo(); c.setLocalVideo(next); setCamOn(next); };
  const flip = () => callRef.current?.cycleCamera({ preferDifferentFacingMode: true });
  const setCamDevice = (id) => callRef.current?.setInputDevicesAsync({ videoDeviceId: id });
  const setMicDevice = (id) => callRef.current?.setInputDevicesAsync({ audioDeviceId: id });

  const leave = async () => {
    await callRef.current?.leave();
    if (role === "coach") setEndOpen(true);
    else { setPhase("ended"); }
  };

  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;
  const cams = devices.filter((d) => d.kind === "videoinput");
  const mics = devices.filter((d) => d.kind === "audioinput");
  const back = role === "coach" ? "/dashboard/livecoach" : "/portal/livecoach";

  if (phase === "loading") return <Shell><div className="text-white/50 animate-pulse pt-32 text-center" data-testid="live-loading">Preparing your Live Coach room…</div></Shell>;
  if (phase === "error") return (
    <Shell>
      <div className="max-w-md mx-auto pt-28 text-center px-5" data-testid="live-error">
        <h1 className="text-xl font-black text-white">Can't open the room</h1>
        <p className="text-sm text-white/55 mt-2">{error}</p>
        <Button onClick={() => nav(back)} variant="outline" className="mt-6 border-white/20 text-white/80"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to sessions</Button>
      </div>
    </Shell>
  );
  if (phase === "ended") return (
    <Shell>
      <div className="max-w-md mx-auto pt-28 text-center px-5" data-testid="live-ended">
        <h1 className="text-2xl font-black text-white">Great session!</h1>
        <p className="text-sm text-white/55 mt-2">Your coach's recap and next actions will appear on your sessions page.</p>
        <Button onClick={() => nav(back)} className="mt-6 bg-emerald-500 hover:bg-emerald-400 text-[#04121f] font-black">View my sessions</Button>
      </div>
    </Shell>
  );

  return (
    <Shell>
      {/* header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-[0.25em] font-black text-emerald-400">BioHacker Live Coach™</div>
          <div className="text-sm font-bold text-white truncate" data-testid="live-objective">{appt?.objective}</div>
        </div>
        <div className="flex items-center gap-3 text-xs shrink-0">
          {phase === "live" && <span className="font-mono text-white/70" data-testid="live-timer">{mmss}</span>}
          <Signal className={`h-4 w-4 ${quality === "low" || quality === "very-low" ? "text-red-400" : "text-emerald-400"}`} data-testid="live-quality" />
        </div>
      </div>

      {phase === "preview" ? (
        <div className="max-w-md mx-auto px-5 pt-8 pb-16" data-testid="live-preview">
          <h1 className="text-xl font-black text-white">Camera & mic check</h1>
          <p className="text-xs text-white/50 mt-1">Frame yourself so your coach can see your full movement. Then join.</p>
          <video ref={localRef} autoPlay muted playsInline className="w-full aspect-video object-cover rounded-xl bg-black mt-4 border border-white/10" />
          <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
            <select onChange={(e) => setCamDevice(e.target.value)} className="rounded-lg bg-white/5 border border-white/15 text-white p-2" data-testid="live-cam-select">
              {cams.map((d) => <option key={d.deviceId} value={d.deviceId} className="bg-[#0b1424]">{d.label || "Camera"}</option>)}
            </select>
            <select onChange={(e) => setMicDevice(e.target.value)} className="rounded-lg bg-white/5 border border-white/15 text-white p-2" data-testid="live-mic-select">
              {mics.map((d) => <option key={d.deviceId} value={d.deviceId} className="bg-[#0b1424]">{d.label || "Microphone"}</option>)}
            </select>
          </div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={flip} className="border-white/20 text-white/80" data-testid="live-flip-preview"><SwitchCamera className="h-4 w-4 mr-1.5" /> Flip</Button>
            <Button onClick={join} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-[#04121f] font-black py-5" data-testid="live-join-btn">Join session</Button>
          </div>
        </div>
      ) : (
        <div className="relative flex-1" data-testid="live-stage">
          <video ref={remoteRef} autoPlay playsInline className="absolute inset-0 w-full h-full object-contain bg-black" />
          {!remoteHere && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-white/50 text-sm animate-pulse" data-testid="live-waiting">Waiting for the other participant to join…</div>
            </div>
          )}
          <video ref={localRef} autoPlay muted playsInline className="absolute bottom-24 right-3 w-28 sm:w-44 aspect-video object-cover rounded-lg border border-white/20 bg-black shadow-xl" />
          {/* controls */}
          <div className="absolute bottom-0 inset-x-0 flex items-center justify-center gap-2 sm:gap-3 pb-5 pt-8" style={{ background: "linear-gradient(transparent, rgba(4,10,20,0.9))" }}>
            <Ctl onClick={toggleMic} active={micOn} testId="live-mic-btn">{micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}</Ctl>
            <Ctl onClick={toggleCam} active={camOn} testId="live-cam-btn">{camOn ? <VideoIcon className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}</Ctl>
            <Ctl onClick={flip} active testId="live-flip-btn"><SwitchCamera className="h-5 w-5" /></Ctl>
            <Ctl onClick={() => setChatOpen(true)} active testId="live-chat-btn"><MessageSquare className="h-5 w-5" /></Ctl>
            {role === "coach" && <Ctl onClick={() => setNotesOpen(true)} active testId="live-notes-btn"><NotebookPen className="h-5 w-5" /></Ctl>}
            {role === "coach" && <Ctl onClick={() => setEndOpen(true)} active testId="live-end-btn"><Flag className="h-5 w-5" /></Ctl>}
            <button onClick={leave} className="h-12 px-5 rounded-full bg-red-500 hover:bg-red-400 text-white flex items-center gap-2 font-black" data-testid="live-leave-btn">
              <PhoneOff className="h-5 w-5" /> {role === "coach" ? "End" : "Leave"}
            </button>
          </div>
        </div>
      )}

      <ChatSheet apptId={apptId} open={chatOpen} onClose={() => setChatOpen(false)} call={callRef} />
      {role === "coach" && <NotesSheet apptId={apptId} open={notesOpen} onClose={() => setNotesOpen(false)} />}
      {role === "coach" && <EndDialog apptId={apptId} open={endOpen} onClose={() => setEndOpen(false)} onDone={() => nav(back)} />}
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#04101d" }}>{children}</div>
);

const Ctl = ({ children, onClick, active, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`h-12 w-12 rounded-full flex items-center justify-center border transition-colors ${active ? "bg-white/10 border-white/20 text-white hover:bg-white/20" : "bg-red-500/20 border-red-400/40 text-red-300"}`}>
    {children}
  </button>
);

const ChatSheet = ({ apptId, open, onClose, call }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const load = () => api.get(`/livecoach/appointments/${apptId}/messages`).then((r) => setMsgs(r.data.messages)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line
  useEffect(() => {
    const c = call.current;
    if (!c) return;
    const h = () => load();
    c.on("app-message", h);
    return () => c.off("app-message", h);
  }, [call]); // eslint-disable-line
  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/livecoach/appointments/${apptId}/messages`, { text: text.trim() });
      try { call.current?.sendAppMessage({ kind: "chat" }, "*"); } catch { /* not joined yet */ }
      setText(""); load();
    } catch (er) { toast.error(formatApiError(er)); }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-y-0 right-0 z-50 w-full sm:w-96 border-l border-white/10 flex flex-col" style={{ backgroundColor: "rgba(4,14,26,0.97)" }} data-testid="live-chat-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <span className="font-black text-white text-sm">Session chat</span>
        <button onClick={onClose} className="text-white/50 hover:text-white text-sm" data-testid="live-chat-close">Close</button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {msgs.map((m) => (
          <div key={m.msg_id} className="rounded-lg bg-white/5 border border-white/10 p-2.5">
            <div className="text-[10px] font-bold text-white/40">{m.from_name}</div>
            <div className="text-sm text-white/85">{m.text}</div>
          </div>
        ))}
      </div>
      <div className="p-3 border-t border-white/10 flex gap-2">
        <Input placeholder="Message or link…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} className="bg-white/5 border-white/15 text-white" data-testid="live-chat-input" />
        <Button onClick={send} className="bg-cyan-500 hover:bg-cyan-400 text-[#04121f] font-black shrink-0" data-testid="live-chat-send">Send</Button>
      </div>
    </div>
  );
};

const NotesSheet = ({ apptId, open, onClose }) => {
  const [text, setText] = useState("");
  const [saved, setSaved] = useState([]);
  const add = async () => {
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/livecoach/appointments/${apptId}/notes`, { text: text.trim() });
      setSaved((s) => [...s, data.note]); setText("");
      toast.success("Note saved (private to you)");
    } catch (er) { toast.error(formatApiError(er)); }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-y-0 right-0 z-50 w-full sm:w-96 border-l border-white/10 flex flex-col" style={{ backgroundColor: "rgba(4,14,26,0.97)" }} data-testid="live-notes-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <span className="font-black text-white text-sm">Coach notes <span className="text-white/40 font-normal">(private)</span></span>
        <button onClick={onClose} className="text-white/50 hover:text-white text-sm">Close</button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {saved.map((n, i) => <div key={i} className="rounded-lg bg-amber-400/5 border border-amber-400/20 p-2.5 text-sm text-white/85">{n.text}</div>)}
      </div>
      <div className="p-3 border-t border-white/10 space-y-2">
        <Textarea placeholder="Timestamped cue, correction, follow-up…" value={text} onChange={(e) => setText(e.target.value)} className="bg-white/5 border-white/15 text-white" data-testid="live-notes-input" />
        <Button onClick={add} className="w-full bg-amber-400 hover:bg-amber-300 text-[#0b1424] font-black" data-testid="live-notes-save">Save note</Button>
      </div>
    </div>
  );
};

const EndDialog = ({ apptId, open, onClose, onDone }) => {
  const [f, setF] = useState({ summary: "", improved: "", continue_focus: "", next_actions: "", objective_status: "improving" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.summary.trim()) return toast.error("Add a short summary for the athlete");
    setBusy(true);
    try {
      await api.post(`/livecoach/appointments/${apptId}/end`, f);
      toast.success("Session completed — recap sent to athlete");
      onDone();
    } catch (er) { toast.error(formatApiError(er)); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white max-w-md">
        <DialogHeader><DialogTitle className="font-black">End session — recap</DialogTitle></DialogHeader>
        <div className="space-y-2.5">
          <Textarea placeholder="Session summary for the athlete *" value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} className="bg-white/5 border-white/15 text-white" data-testid="live-recap-summary" />
          <Input placeholder="What improved" value={f.improved} onChange={(e) => setF({ ...f, improved: e.target.value })} className="bg-white/5 border-white/15 text-white" data-testid="live-recap-improved" />
          <Input placeholder="Keep working on" value={f.continue_focus} onChange={(e) => setF({ ...f, continue_focus: e.target.value })} className="bg-white/5 border-white/15 text-white" />
          <Input placeholder="Next actions / assignment" value={f.next_actions} onChange={(e) => setF({ ...f, next_actions: e.target.value })} className="bg-white/5 border-white/15 text-white" data-testid="live-recap-next" />
          <select value={f.objective_status} onChange={(e) => setF({ ...f, objective_status: e.target.value })} className="w-full rounded-lg bg-white/5 border border-white/15 text-white text-sm p-2.5" data-testid="live-recap-status">
            {["achieved", "improving", "unchanged", "follow_up"].map((v) => <option key={v} value={v} className="bg-[#0b1424]">{v.replace("_", " ")}</option>)}
          </select>
          <Button onClick={submit} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#04121f] font-black" data-testid="live-recap-submit">{busy ? "Saving…" : "Complete session"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
